// PINNED BOARD ITEMS on the home (DESIGN-WL-ASSISTANT-MAIN-HOME-1 — the shell
// half of the widgetDefs contract CORE-WIDGETDEFS-ACCEPT-BOARD-ITEMS-1, live on
// dev since server c9b2ea7).
//
// A pin (functions/assistantBoard.js pinBoardItem → aiCreateWidget) writes ONE
// `workspaceAiWidgets` row, and the server answers with its `kind`:
//   'widget' { widget: '<app>.<widget id>', params }  an installed app's widget
//   'block'  { block: { type, data, format?, source? } }  the values it showed
//   'recipe' { dataRecipe, body }                      the older AI widgets
// dataRecipe / body are null for 'widget' and 'block', so the home cannot hand
// those rows to WidgetRenderer as they are. homePinnedHostRow turns a row into
// what the home host draws — the SAME card the board drew:
//   widget → the app's own widget entry (module, body, params schema) with the
//            pinned params: the module host mounts it with frame contract v1
//            (scope.params = the schema's defaults + these values);
//   block  → the ONE reviewed block definition (the board's own validator) and
//            its static data (`blockData`), which the host seeds into the
//            AI-widget data bag before WidgetRenderer mounts;
//   an app that is not installed (any more) → the row as it is: WidgetRenderer
//            draws the server's capability notice (viewerCapability
//            not_installed), never a blank card.
//
// FRANK: every export is self-contained (FA204); the board's validator is a
// cross-file import.
import { validateBoardSpec } from './assistantBoard.js'
import { recordsContentSize } from './assistantRecords.js'

// A stored block → { type, def, data } through the board's own validator, or
// null when nothing renders. The server's canonical data goes in as it is: a
// chip-row's { chips: [...] } is read by the board's ONE chip reader
// (assistantBoard.js validateBoardSpec, rowsOf), never by a copy here.
export const homePinnedBlock = (block) => {
  if (!block || typeof block !== 'object' || typeof block.type !== 'string') return null
  const data = block.data
  const res = validateBoardSpec.call(
    null,
    { items: [{ kind: 'block', block: block.type, data, format: block.format, source: block.source || {} }] },
    { widgets: {} }
  )
  const it = res && Array.isArray(res.items) ? res.items[0] : null
  if (!it || it.kind !== 'block') return null
  return { type: it.block, def: it.def, data: it.data }
}

// A stored AI row → the row the home host draws. `moduleRows` are the module
// widget rows the home already resolved (pages/main.js _allModuleWidgetsFor:
// `{ …manifest entry, moduleId }`).
export const homePinnedHostRow = (row, moduleRows) => {
  if (!row || typeof row !== 'object') return row
  const kind = row.kind || (typeof row.widget === 'string' ? 'widget' : row.block && typeof row.block === 'object' ? 'block' : 'recipe')
  if (kind === 'widget' && typeof row.widget === 'string') {
    const at = row.widget.lastIndexOf('.')
    const app = at > 0 ? row.widget.slice(0, at) : ''
    const id = at > 0 ? row.widget.slice(at + 1) : ''
    const aliases = (moduleId) => {
      const full = String(moduleId || '')
      const key = full.split('/').pop()
      return [full, key, key.replace(/-app$/, '')]
    }
    const entry = (Array.isArray(moduleRows) ? moduleRows : []).find(
      (w) => w && w.id === id && w.moduleId && aliases(w.moduleId).includes(app)
    )
    if (!entry) return { ...row, kind }
    const layout = {}
    for (const k of ['w', 'h', 'minW', 'minH', 'maxW', 'maxH']) {
      if (row.spec && Number.isFinite(row.spec[k])) layout[k] = row.spec[k]
    }
    return {
      ...row,
      kind,
      moduleId: entry.moduleId,
      body: entry.body,
      spec: { ...(entry.spec || {}), ...layout, params: entry.params || (entry.spec && entry.spec.params) || {} },
      params: row.params && typeof row.params === 'object' ? row.params : {},
      title: row.title || entry.title,
      icon: row.icon || entry.icon,
      hue: row.hue || entry.hue || null,
      pinnedFrom: row.widget
    }
  }
  // A RECORDS row (WIDGET-TOOLS-RECORDS-SHAPE-1) takes the block's CONTENT
  // size — the board's own rule — as its size and its floor (a chart in an S
  // cell was squeezed; dev 2026-10-04). Its hue stays its own: without one the
  // records block draws in the text ink (AssistantRecordsBlock).
  if (kind === 'records' && row.records && typeof row.records === 'object') {
    const size = recordsContentSize(row.records)
    return { ...row, kind, size, minSize: size }
  }
  if (kind === 'block') {
    // FA204 — the block read is restated here (homePinnedBlock's body): a
    // same-file sibling export does not survive frank.toJSON.
    const block = row.block
    if (!block || typeof block !== 'object' || typeof block.type !== 'string') return { ...row, kind }
    const data = block.data
    const res = validateBoardSpec.call(
      null,
      { items: [{ kind: 'block', block: block.type, data, format: block.format, source: block.source || {} }] },
      { widgets: {} }
    )
    const it = res && Array.isArray(res.items) ? res.items[0] : null
    if (!it || it.kind !== 'block') return { ...row, kind }
    return { ...row, kind, body: { blocks: [it.def] }, blockData: it.data }
  }
  return row
}
